import { Alert, Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAxios } from 'configs/requests';
import { useCanMigratePatients } from 'hooks';
import { Error, Loading } from 'components/Fetch';
import { getMigrationStatus, isMigrationOutstanding } from 'utils/PatientMigration';

interface IUnassignDeviceProps {
  deviceId: string;
  isGlucometer: boolean;
  /** Named so the confirmation says which device, on a list where rows look alike. */
  deviceName?: string;
  imei?: string;
  patient?: IDevicePatientDto;
  onCancel: () => void;
  onUnassign: () => void;
}

interface IMeasurementCountResponse {
  count: number;
}

const UnassignDevice = ({
  deviceId,
  isGlucometer,
  deviceName,
  imei,
  patient,
  onCancel,
  onUnassign
}: IUnassignDeviceProps) => {
  const intl = useIntl();
  const canMigratePatients = useCanMigratePatients();

  // The device list carries only a thin patient DTO with no migration marker, and this modal is
  // the only route to unassign, so the marker is fetched here rather than threaded through a
  // list that does not have it. Only for an operator who can act on a migration.
  const [{ data: patientData, loading: loadingPatient }] = useAxios<IPatient>(
    { method: 'GET', url: `/clinician/Patient/${patient?.uid}` },
    { manual: !canMigratePatients || !patient?.uid, useCache: true }
  );
  const migrationStatus = getMigrationStatus(patientData);

  const [{ data, loading, error }, getMeasurementCount] = useAxios<IMeasurementCountResponse>(
    {
      url: `/clinician-gateway/device/${deviceId}/measurement-count`,
      method: 'GET'
    },
    { manual: !isGlucometer, useCache: false }
  );

  const [{ loading: loadingUnassign, error: errorUnassign }, postUnassign] = useAxios<void>(
    {
      url: `/clinician-gateway/device/${deviceId}/unassign`,
      method: 'POST'
    },
    { manual: true }
  );

  const handleUnassign = () => {
    postUnassign().then(() => {
      onUnassign();
    });
  };

  // displayName is a translation key by design here (bt106BPDisplayKey); defaultMessage covers
  // a device whose name has no key yet.
  const deviceLabel = deviceName
    ? intl.formatMessage({ id: deviceName, defaultMessage: deviceName })
    : undefined;

  const readings = data?.count ?? 0;
  const showSuspendWarning = isGlucometer && readings > 0;
  // The migration warning depends on this fetch, so it has to hold the confirm button too.
  // Without it the operator can read "are you sure", click Unassign, and never see the warning
  // that the device is part of a move: the whole point of the lookup.
  const isLoading =
    (isGlucometer && loading) || loadingUnassign || (canMigratePatients && loadingPatient);

  return (
    <Modal show onHide={onCancel} data-test="unassign-modal">
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({ id: 'unassignConfirmationTitleKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && <Loading />}
        {error && (
          <Error
            errorMessage={intl.formatMessage({ id: 'errorFetchingDeviceKey' })}
            retry={() => getMeasurementCount()}
          />
        )}
        {errorUnassign && (
          <Error
            errorMessage={intl.formatMessage({ id: 'errorUnassigningDeviceKey' })}
            retry={() => postUnassign()}
          />
        )}
        {!isLoading && !error && !errorUnassign && (
          <>
            {canMigratePatients && (patient || deviceName || imei) && (
              <div className="fw-bold mb-2" data-test="unassign-subject">
                {[patient && `${patient.firstName} ${patient.lastName}`.trim(), deviceLabel, imei]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
            {isMigrationOutstanding(migrationStatus) && (
              <Alert variant="warning" data-test="unassign-migration-warning">
                {intl.formatMessage(
                  { id: 'migrationUnassignWarningKey' },
                  { destination: migrationStatus?.destinationEnvironment ?? '' }
                )}
              </Alert>
            )}
            {showSuspendWarning ? (
              <div>{intl.formatMessage({ id: 'unassignDeviceReadingsKey' }, { readings })}</div>
            ) : (
              <div>{intl.formatMessage({ id: 'patientWillNotBeLinkedKey' })}</div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} data-test="unassign-cancel">
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
        <Button
          variant={showSuspendWarning ? 'danger' : 'primary'}
          onClick={handleUnassign}
          data-test="unassign-confirm"
          disabled={isLoading}
        >
          {intl.formatMessage({ id: 'UnassignKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnassignDevice;
